import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Paginator } from 'src/app/shared/models/paginator';
import { TableHeader } from 'src/app/shared/models/table';
import { PageableService } from 'src/app/shared/services/pageable.service';
import { environment } from 'src/environments/environment';
import { Pageable } from '../../knowledge-bank/explore/models/paginator';
import { User } from '../../user/user';
import { AreaKnowledgeBank } from './area-knowledge-bank';


type AreaQuery = {
  pagina?: number;
  tamanoPagina?: number;
  columna?: string;
  valor?: string;
  ordenamiento?: string; // ej: "nombreArea,ASC"
};

@Injectable({
  providedIn: 'root'
})
export class AreaKnowledgeBankService {
  public areaKnowledgeBanksUrl = `${environment.urlBase}/area-banco-conocimiento/`;
  public areaKnowledgeBankUsersUrl = `${environment.urlBase}/usuario-area-banco-conocimiento/`;

  constructor(private http: HttpClient, private pageableService: PageableService) { }
  /**
   * This function gets the list of knowledge banks by page
   * It can get some params in order to filter or sort the data
   * @param {number} page
   * @param {number} pageQuantity
   * @param {string} header
   * @param {string} filter
   * @param {string} order
   * @returns {Observable<Paginator<AreaKnowledgeBank>>}
   */
  getAreaKnowledgeBanks(page: number, pageQuantity: number, header?: string, filter?: string, order?: string): Observable<Paginator<AreaKnowledgeBank>> {
    return this.pageableService.getData<AreaKnowledgeBank>(this.areaKnowledgeBanksUrl, page, pageQuantity, header, filter, order);
  }

  /**
   * Gets all the list of knowledge banks sending filters
   * @param {BehaviorSubject} search$
   * @param {number} page
   * @returns {Observable<Paginator<AreaKnowledgeBank>>}
   */
  getAreaKnowledgeBankListByFilters(search$: BehaviorSubject<TableHeader | undefined>, page: number, pageQuantity: number): Observable<Paginator<AreaKnowledgeBank>> {
    return this.pageableService.searchData<AreaKnowledgeBank>(search$, page, pageQuantity, this.areaKnowledgeBanksUrl);
  }

  /**
   * This function creates a knowledge bank
   * @param {AreaKnowledgeBank} AreaKnowledgeBank
   * @returns {Observable<AreaKnowledgeBank>}
   */
  createAreaKnowledgeBank(AreaKnowledgeBank: AreaKnowledgeBank): Observable<AreaKnowledgeBank> {
    return this.http.post<AreaKnowledgeBank>(this.areaKnowledgeBanksUrl, AreaKnowledgeBank);
  }

  /**
   * This function updates a knowledge bank
   * @param {AreaKnowledgeBank} AreaKnowledgeBank
   * @returns {Observable<AreaKnowledgeBank>}
   */
  updateAreaKnowledgeBank(AreaKnowledgeBank: AreaKnowledgeBank): Observable<AreaKnowledgeBank> {
    return this.http.put<AreaKnowledgeBank>(this.areaKnowledgeBanksUrl, AreaKnowledgeBank);
  }

  /**
   * Get all the knowledge bank users by page
   * It can get some params in order to filter or sort the data
   * @param {number} page
   * @param {number} pageQuantity
   * @param {string} header
   * @param {string} filter
   * @param {string} order
   * @returns {Observable<Paginator<User>>}
   */
  getUsers(page: number, pageQuantity: number, header?: string, filter?: string, order?: string): Observable<Paginator<User>> {
    return this.pageableService.getData<User>(this.areaKnowledgeBankUsersUrl, page, pageQuantity, header, filter, order);
  }

  /**
   * Gets the list of users associated with a knowledge bank, sending filters
   * @param {BehaviorSubject} search$
   * @param {number} page
   * @param {number} pageQuantity
   * @param {string} areaKnowledgeBankId
   * @returns {Observable<Paginator<User>>}
   */
  getUsersByFilters(search$: BehaviorSubject<TableHeader | undefined>, page: number, pageQuantity: number, areaKnowledgeBankId: string): Observable<Paginator<User>> {
    return this.pageableService.searchData<User>(search$, page, pageQuantity, `${this.areaKnowledgeBankUsersUrl}area/${areaKnowledgeBankId}`);
  }

  /**
   * Returns the endpoint to get users not associated to a knowledge bank
   * @param {string} areaKnowledgeBankId
   * @returns {string}
   */
  usersNotAssociatedUrl(areaKnowledgeBankId: string): string {
    return `${this.areaKnowledgeBankUsersUrl}no-asociados/${areaKnowledgeBankId}`;
  }

  /**
   * A function to associate the user to a knowledge bank
   * @param {AreaKnowledgeBankId} areaKnowledgeBankId
   * @param {string} userUuid
   * @returns {Observable<{}>}
   */
  addUserToAreaKnowledgeBank(areaKnowledgeBankId: string, userUuid: string): Observable<object> {
    return this.http.put(`${this.areaKnowledgeBankUsersUrl}${areaKnowledgeBankId}/agregar/${userUuid}`, null);
  }

  /**
   * This function removes the user from the current knowledge bank
   * @param {string} areaKnowledgeBankId
   * @param {string} userUuid
   * @returns {Observable<{}>}
   */
  deleteUserFromAreaKnowledgeBank(areaKnowledgeBankId: string, userUuid: string): Observable<object> {
    return this.http.delete(`${this.areaKnowledgeBankUsersUrl}${areaKnowledgeBankId}/eliminar/${userUuid}`);
  }

  /**
   * Gets selected knowledge bank
   * @param {string} areaKnowledgeBankId
   * @returns {Observable<AreaKnowledgeBank>}
   */
  getAreaKnowledgeBank(areaKnowledgeBankId: string): Observable<AreaKnowledgeBank> {
    return this.http.get<AreaKnowledgeBank>(`${this.areaKnowledgeBanksUrl}${areaKnowledgeBankId}`);
  }

  /**
   * Listado de áreas por usuario (PageImpl)
   * GET /usuario/{usuarioUuid}?pagina=&tamanoPagina=&columna=&valor=&ordenamiento=
   */
  getAreasByUser(usuarioUuid: string, q: AreaQuery = {}): Observable<Pageable<AreaKnowledgeBank>> {
    let params = new HttpParams()
      .set('pagina', String(q.pagina ?? 0))
      .set('tamanoPagina', String(q.tamanoPagina ?? 10));

    if (q.columna) params = params.set('columna', q.columna);
    if (q.valor) params = params.set('valor', q.valor);
    // if (q.ordenamiento) params = params.set('ordenamiento', q.ordenamiento);

    return this.http.get<Pageable<AreaKnowledgeBank>>(
      `${this.areaKnowledgeBankUsersUrl}usuario/${usuarioUuid}`,
      { params }
    );
  }

  /**
   * Add all knowledge banks to the current user
   * @param {string} userUuid
   * @returns {Observable<AreaKnowledgeBank[]>}
   */
  addAllAreaKnowledgeBanksToUser(userUuid: string): Observable<AreaKnowledgeBank[]> {
    return this.http.put<AreaKnowledgeBank[]>(`${this.areaKnowledgeBanksUrl}asignar-todo/usuario/${userUuid}`, null);
  }

  deleteAreaKnowledgeBank(areaUuid: string):  Observable<AreaKnowledgeBank>{
    return this.http.delete<AreaKnowledgeBank>(`${this.areaKnowledgeBanksUrl}${areaUuid}`);
  }
}